import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  TextInput,
  Modal,
  Alert,
  Image,
  Animated,
  Dimensions,
  RefreshControl,
  ActivityIndicator,
  StatusBar,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import CancelSubscriptionScreen from './CancelSubscriptionScreen';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../utils/iosCommerce';

const { width, height } = Dimensions.get('window');

// API Configuration
const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Brand color palette
const colors = {
  primary: '#0e4e96',
  primaryLight: '#4a7db5',
  secondary: '#7C3AED',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
};

const { primary, secondary, success, danger, warning, gray } = colors;

// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

export default function PaymentsScreen({ navigation, route }) {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Main states
  const [activeTab, setActiveTab] = useState('subscription');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [businessId, setBusinessId] = useState(null);

  // Subscription states
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  const [nextBillingDate, setNextBillingDate] = useState(null);

  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Billing history
  const [billingHistory, setBillingHistory] = useState([]);

  // Premium features (static)
  const premiumFeatures = [
    {
      id: 'ai_resume',
      title: 'AI Resume Optimization',
      description: 'Get your resume optimized by AI for better ATS compatibility',
      icon: 'document-text',
    },
    {
      id: 'unlimited_apps',
      title: 'Unlimited Applications',
      description: 'Apply to unlimited jobs without restrictions',
      icon: 'infinite',
    },
    {
      id: 'premium_templates',
      title: 'Premium Templates',
      description: 'Access to professional resume and cover letter templates',
      icon: 'color-palette',
    },
    {
      id: 'analytics',
      title: 'Advanced Analytics',
      description: 'Detailed insights into your job search performance',
      icon: 'analytics',
    },
    {
      id: 'coaching',
      title: 'Career Coaching',
      description: 'Get personalized career advice and interview preparation',
      icon: 'school',
    },
    {
      id: 'networking',
      title: 'Networking Events',
      description: 'Access to exclusive networking events and webinars',
      icon: 'people',
    },
  ];

  // Modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Form states
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cardForm, setCardForm] = useState({
    number: '',
    expiry: '',
    cvv: '',
    name: '',
    isDefault: false,
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const tabIndicatorAnim = useRef(new Animated.Value(0)).current;

  // Load business ID on mount
  useEffect(() => {
    loadBusinessId();
  }, []);

  const loadBusinessId = async () => {
    try {
      const companyData = await AsyncStorage.getItem('company_profile');
      if (companyData) {
        const company = JSON.parse(companyData);
        if (company && company.id) {
          setBusinessId(company.id);
          return;
        }
      }
      // Secondary fallback: a generic company_id key written by other flows.
      const cid = await AsyncStorage.getItem('company_id');
      const parsed = cid ? parseInt(cid, 10) : null;
      if (parsed && parsed > 0) {
        setBusinessId(parsed);
        return;
      }
      // No company context available — do NOT fall back to 1. Falling back to 1
      // routes a logged-in company-9 user's writes to company 1 (the test tenant).
      // The useFocusEffect gate (`if (businessId) loadAllData()`) already
      // handles null gracefully by showing the empty/loading state.
      setBusinessId(null);
    } catch (error) {
      console.error('Error loading business ID:', error);
      setBusinessId(null);
    }
  };

  // Load all data when businessId is available or screen focused
  useFocusEffect(
    React.useCallback(() => {
      if (businessId) {
        loadAllData();
      }
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
      ]).start();
    }, [businessId])
  );

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSubscription(),
        fetchPlans(),
        fetchPaymentMethods(),
        fetchBillingHistory(),
      ]);
    } catch (error) {
      console.error('Error loading payment data:', error);
      Alert.alert('Error', 'Failed to load payment information.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_BASE}/business-subscriptions?company_id=${businessId}`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success && data.data.length > 0) {
        const sub = data.data[0];
        setSubscription(sub);
        setCurrentPlan(sub.plan_id || 'free');
        setSubscriptionStatus(sub.status);
        setNextBillingDate(sub.end_date);
      } else {
        setSubscription(null);
        setCurrentPlan('free');
        setSubscriptionStatus('inactive');
        setNextBillingDate(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_BASE}/subscription-plans`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success) {
        const plans = data.data.map(plan => ({
          id: plan.plan_id,
          name: plan.name,
          price: `£${parseFloat(plan.price).toFixed(2)}`,
          period: plan.billing_cycle,
          features: plan.features,
          color: plan.category === 'business' ? primary : plan.category === 'professional' ? secondary : success,
          popular: plan.plan_id.includes('professional') || plan.plan_id.includes('enterprise'),
        }));
        setSubscriptionPlans(plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetch(`${API_BASE}/payment-methods?company_id=${businessId}`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success) {
        setPaymentMethods(data.data);
      } else {
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/invoices?company_id=${businessId}`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success) {
        setBillingHistory(data.data);
      } else {
        setBillingHistory([]);
      }
    } catch (error) {
      console.error('Error fetching billing history:', error);
      setBillingHistory([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleUpgradePlan = async (planId) => {
    if (!businessId) {
      Alert.alert('Error', 'Business ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/business-subscriptions`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: businessId,
          plan_id: planId,
          billing_cycle: 'monthly',
          payment_method: 'card',
        }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Upgrade failed');

      setShowUpgradeModal(false);
      await fetchSubscription();
      Alert.alert('Success', `Successfully upgraded to ${subscriptionPlans.find(p => p.id === planId)?.name} plan!`);
    } catch (error) {
      console.error('Upgrade error:', error);
      Alert.alert('Error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    setShowCancelModal(false);
    await fetchSubscription();
    Alert.alert('Subscription Updated', 'Your subscription status has been updated.');
  };

  const handleAddPaymentMethod = async () => {
    if (!businessId) {
      Alert.alert('Error', 'Business ID not found.');
      return;
    }

    if (!cardForm.number || !cardForm.expiry || !cardForm.cvv || !cardForm.name) {
      Alert.alert('Error', 'Please fill in all card details');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/payment-methods`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: businessId,
          type: 'card',
          details: {
            cardNumber: cardForm.number.replace(/\s/g, ''),
            expiryMonth: cardForm.expiry.split('/')[0],
            expiryYear: cardForm.expiry.split('/')[1],
            cvv: cardForm.cvv,
            name: cardForm.name,
          },
          is_default: cardForm.isDefault,
        }),
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to add payment method');

      await fetchPaymentMethods();
      setShowAddCardModal(false);
      setCardForm({ number: '', expiry: '', cvv: '', name: '', isDefault: false });
      Alert.alert('Success', 'Payment method added successfully!');
    } catch (error) {
      console.error('Add payment method error:', error);
      Alert.alert('Error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    Alert.alert(
      'Remove Payment Method',
      'Are you sure you want to remove this payment method?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const response = await fetch(`${API_BASE}/payment-methods/${methodId}`, {
                method: 'DELETE',
                headers: { 'X-API-Key': API_KEY },
              });
              const data = await response.json();
              if (!data.success) throw new Error('Failed to delete');
              await fetchPaymentMethods();
            } catch (err) {
              Alert.alert('Error', 'Could not remove payment method.');
            }
          },
        },
      ]
    );
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      const response = await fetch(`${API_BASE}/payment-methods/${methodId}/default`, {
        method: 'PUT',
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (!data.success) throw new Error('Failed to set default');
      await fetchPaymentMethods();
    } catch (err) {
      Alert.alert('Error', 'Could not set default payment method.');
    }
  };

  const formatCardNumber = (text) => {
    const cleaned = text.replace(/\s/g, '');
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(' ') : cleaned;
  };

  const formatExpiry = (text) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.substring(0, 2) + '/' + cleaned.substring(2, 4);
    }
    return cleaned;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return success;
      case 'cancelled': return danger;
      case 'past_due': return warning;
      default: return gray[500];
    }
  };

  // Header
  const renderHeader = () => (
    <LinearGradient
      colors={[primary, primary]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
      style={styles.header}
    >
      <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>Payments & Billing</Text>
      <View style={styles.headerRight}>
        <JobengLogo />
      </View>
    </LinearGradient>
  );

  // Tabs with animated indicator
  const renderTabs = () => {
    const tabs = [
      { key: 'subscription', label: 'Subscription', icon: 'card' },
      { key: 'methods', label: 'Payment Methods', icon: 'wallet' },
      { key: 'history', label: 'Billing History', icon: 'receipt' },
      { key: 'features', label: 'Premium Features', icon: 'star' },
    ];
    const tabWidth = width / tabs.length;

    return (
      <View style={styles.tabContainer}>
        {tabs.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={styles.tab}
            onPress={() => {
              setActiveTab(tab.key);
              Animated.spring(tabIndicatorAnim, {
                toValue: index * tabWidth,
                useNativeDriver: true,
                damping: 20,
                stiffness: 150,
              }).start();
            }}
          >
            <Ionicons
              name={tab.icon}
              size={20}
              color={activeTab === tab.key ? primary : gray[500]}
            />
            <Text
              style={[
                styles.tabLabel,
                { color: activeTab === tab.key ? primary : gray[600] },
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
        <Animated.View
          style={[
            styles.tabIndicator,
            { width: tabWidth - 40, transform: [{ translateX: tabIndicatorAnim }] },
          ]}
        />
      </View>
    );
  };

  // Subscription Tab
  const renderSubscriptionTab = () => (
    <ScrollView
      style={styles.tabContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
      showsVerticalScrollIndicator={false}
    >
      {/* Current Subscription Card */}
      <LinearGradient
        colors={subscription ? [primary, primaryLight] : [gray[600], gray[500]]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.currentSubscriptionCard}
      >
        <View style={styles.subscriptionHeader}>
          <View>
            <Text style={styles.currentPlanLabel}>Current Plan</Text>
            <Text style={styles.currentPlanName}>
              {subscription ? subscription.plan_id?.replace('multi_', '').toUpperCase() : 'Free'}
            </Text>
          </View>
          {subscription && (
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(subscription.status) }]}>
              <Text style={styles.statusText}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </Text>
            </View>
          )}
        </View>

        {subscription && subscription.status !== 'inactive' && (
          <View style={styles.billingInfo}>
            <View style={styles.billingRow}>
              <Ionicons name="calendar-outline" size={16} color="#fff" />
              <Text style={styles.billingLabel}>Next billing:</Text>
              <Text style={styles.billingValue}>
                {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : 'N/A'}
              </Text>
            </View>
            <View style={styles.billingRow}>
              <Ionicons name="cash-outline" size={16} color="#fff" />
              <Text style={styles.billingLabel}>Amount:</Text>
              <Text style={styles.billingValue}>
                £{subscription.price_paid}/{subscription.billing_cycle}
              </Text>
            </View>
          </View>
        )}

        <View style={styles.subscriptionActions}>
          {!subscription || subscription.status === 'inactive' ? (
            <TouchableOpacity style={styles.upgradeButton} onPress={() => setShowUpgradeModal(true)}>
              <Ionicons name="arrow-up" size={20} color="#fff" />
              <Text style={styles.upgradeButtonText}>Upgrade Plan</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.actionButtons}>
              <TouchableOpacity style={styles.changeButton} onPress={() => setShowUpgradeModal(true)}>
                <Text style={styles.changeButtonText}>Change</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.cancelButton} onPress={handleCancelSubscription}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </LinearGradient>

      {/* Usage Stats Card */}
      <View style={styles.usageCard}>
        <Text style={styles.usageTitle}>This Month's Usage</Text>
        <View style={styles.usageStats}>
          <View style={styles.usageStat}>
            <Text style={styles.usageNumber}>47</Text>
            <Text style={styles.usageLabel}>Applications</Text>
          </View>
          <View style={styles.usageStat}>
            <Text style={styles.usageNumber}>12</Text>
            <Text style={styles.usageLabel}>Downloads</Text>
          </View>
          <View style={styles.usageStat}>
            <Text style={styles.usageNumber}>8</Text>
            <Text style={styles.usageLabel}>AI Optimizations</Text>
          </View>
        </View>
      </View>

      {/* Available Plans */}
      <Text style={styles.sectionTitle}>Available Plans</Text>
      {subscriptionPlans.map((plan) => {
        const isCurrent = currentPlan === plan.id;
        const isPopular = plan.popular && !isCurrent;
        return (
          <View key={plan.id} style={styles.planCard}>
            {isPopular && (
              <View style={styles.popularBadge}>
                <Text style={styles.popularBadgeText}>MOST POPULAR</Text>
              </View>
            )}
            <View style={styles.planHeader}>
              <View>
                <Text style={styles.planName}>{plan.name}</Text>
                <View style={styles.planPriceRow}>
                  <Text style={[styles.planPrice, { color: plan.color }]}>{plan.price}</Text>
                  <Text style={styles.planPeriod}>/{plan.period}</Text>
                </View>
              </View>
              {isCurrent ? (
                <View style={[styles.currentPlanChip, { backgroundColor: success }]}>
                  <Text style={styles.currentPlanChipText}>Current</Text>
                </View>
              ) : (
                <TouchableOpacity
                  style={[styles.selectPlanButton, { backgroundColor: plan.color }]}
                  onPress={() => {
                    setSelectedPlan(plan.id);
                    setShowUpgradeModal(true);
                  }}
                >
                  <Text style={styles.selectPlanButtonText}>Select</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.featureList}>
              {plan.features.slice(0, 4).map((feature, idx) => (
                <View key={idx} style={styles.featureItem}>
                  <Ionicons name="checkmark-circle" size={16} color={plan.color} />
                  <Text style={styles.featureText}>{feature}</Text>
                </View>
              ))}
              {plan.features.length > 4 && (
                <Text style={styles.moreFeatures}>+{plan.features.length - 4} more</Text>
              )}
            </View>
          </View>
        );
      })}
    </ScrollView>
  );

  // Payment Methods Tab
  const renderPaymentMethodsTab = () => (
    <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Your Cards</Text>
        <TouchableOpacity style={styles.addButton} onPress={() => setShowAddCardModal(true)}>
          <Ionicons name="add" size={20} color={primary} />
          <Text style={styles.addButtonText}>Add New</Text>
        </TouchableOpacity>
      </View>

      {paymentMethods.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="card-outline" size={48} color={gray[400]} />
          <Text style={styles.emptyStateText}>No payment methods yet</Text>
        </View>
      ) : (
        paymentMethods.map((method) => (
          <View key={method.id} style={styles.paymentMethodCard}>
            <View style={styles.cardInfo}>
              <View style={styles.cardIcon}>
                <Ionicons name="card" size={24} color={primary} />
              </View>
              <View style={styles.cardDetails}>
                <Text style={styles.cardNumber}>
                  {method.type?.toUpperCase()} •••• {method.last4}
                </Text>
                <Text style={styles.cardExpiry}>
                  Expires {method.expiryMonth}/{method.expiryYear}
                </Text>
              </View>
            </View>
            <View style={styles.cardActions}>
              {method.isDefault && (
                <View style={styles.defaultBadge}>
                  <Text style={styles.defaultBadgeText}>Default</Text>
                </View>
              )}
              <TouchableOpacity onPress={() => handleRemovePaymentMethod(method.id)}>
                <Ionicons name="trash-outline" size={20} color={danger} />
              </TouchableOpacity>
            </View>
          </View>
        ))
      )}

      <View style={styles.securityNote}>
        <Ionicons name="shield-checkmark" size={20} color={success} />
        <Text style={styles.securityNoteText}>
          Your payment info is encrypted and secure.
        </Text>
      </View>
    </ScrollView>
  );

  // Billing History Tab
  const renderBillingHistoryTab = () => (
    <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Invoices</Text>
        <TouchableOpacity style={styles.downloadButton}>
          <Ionicons name="download-outline" size={20} color={primary} />
          <Text style={styles.downloadButtonText}>Export</Text>
        </TouchableOpacity>
      </View>

      {billingHistory.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="receipt-outline" size={48} color={gray[400]} />
          <Text style={styles.emptyStateText}>No billing history yet</Text>
        </View>
      ) : (
        billingHistory.map((invoice) => (
          <View key={invoice.id} style={styles.invoiceCard}>
            <View style={styles.invoiceHeader}>
              <View>
                <Text style={styles.invoiceDate}>{new Date(invoice.date).toLocaleDateString()}</Text>
                <Text style={styles.invoiceDescription}>{invoice.description}</Text>
              </View>
              <View style={styles.invoiceRight}>
                <Text style={styles.invoiceAmount}>£{invoice.amount}</Text>
                <View style={[styles.invoiceStatusBadge, { backgroundColor: getStatusColor(invoice.status) }]}>
                  <Text style={styles.invoiceStatusText}>{invoice.status}</Text>
                </View>
              </View>
            </View>
            <Text style={styles.invoiceNumber}>Invoice #{invoice.number}</Text>
          </View>
        ))
      )}
    </ScrollView>
  );

  // Premium Features Tab
  const renderPremiumFeaturesTab = () => {
    const isPaid = currentPlan !== 'free';
    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
        <Text style={styles.sectionTitle}>Unlock More with Premium</Text>
        {premiumFeatures.map((feature) => {
          const unlocked = isPaid;
          return (
            <View key={feature.id} style={[styles.featureCard, !unlocked && styles.featureLocked]}>
              <View style={styles.featureRow}>
                <View style={[styles.featureIcon, { backgroundColor: unlocked ? primary : gray[200] }]}>
                  <Ionicons
                    name={feature.icon}
                    size={24}
                    color={unlocked ? '#fff' : gray[500]}
                  />
                </View>
                <View style={styles.featureInfo}>
                  <Text style={[styles.featureTitle, !unlocked && styles.featureTitleLocked]}>
                    {feature.title}
                  </Text>
                  <Text style={[styles.featureDescription, !unlocked && styles.featureDescriptionLocked]}>
                    {feature.description}
                  </Text>
                </View>
                {!unlocked && (
                  <Ionicons name="lock-closed" size={20} color={gray[400]} />
                )}
              </View>
            </View>
          );
        })}
      </ScrollView>
    );
  };

  // Upgrade Modal
  const renderUpgradeModal = () => (
    <Modal
      visible={showUpgradeModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowUpgradeModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Choose a Plan</Text>
            <TouchableOpacity onPress={() => setShowUpgradeModal(false)}>
              <Ionicons name="close" size={24} color={gray[600]} />
            </TouchableOpacity>
          </View>
          <ScrollView showsVerticalScrollIndicator={false}>
            {subscriptionPlans.filter(p => p.id !== 'free').map((plan) => (
              <TouchableOpacity
                key={plan.id}
                style={[styles.modalPlanCard, selectedPlan === plan.id && styles.modalPlanSelected]}
                onPress={() => setSelectedPlan(plan.id)}
              >
                <View style={styles.modalPlanHeader}>
                  <View>
                    <Text style={styles.modalPlanName}>{plan.name}</Text>
                    <Text style={[styles.modalPlanPrice, { color: plan.color }]}>{plan.price}</Text>
                  </View>
                  <View style={[styles.radio, selectedPlan === plan.id && styles.radioSelected]}>
                    {selectedPlan === plan.id && <View style={styles.radioInner} />}
                  </View>
                </View>
                <View style={styles.modalFeatureList}>
                  {plan.features.slice(0, 3).map((f, i) => (
                    <Text key={i} style={styles.modalFeatureItem}>• {f}</Text>
                  ))}
                  {plan.features.length > 3 && (
                    <Text style={styles.modalFeatureMore}>+{plan.features.length - 3} more</Text>
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.modalButton, loading && styles.modalButtonDisabled]}
              onPress={() => handleUpgradePlan(selectedPlan)}
              disabled={loading || !selectedPlan}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.modalButtonText}>Upgrade Now</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Add Card Modal
  const renderAddCardModal = () => (
    <Modal
      visible={showAddCardModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowAddCardModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Payment Method</Text>
            <TouchableOpacity onPress={() => setShowAddCardModal(false)}>
              <Ionicons name="close" size={24} color={gray[600]} />
            </TouchableOpacity>
          </View>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Card Number</Text>
              <TextInput
                style={styles.formInput}
                placeholder="1234 5678 9012 3456"
                value={cardForm.number}
                onChangeText={(t) => setCardForm({ ...cardForm, number: formatCardNumber(t.replace(/\s/g, '').substring(0, 16)) })}
                keyboardType="numeric"
                maxLength={19}
              />
            </View>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, { flex: 1, marginRight: 8 }]}>
                <Text style={styles.formLabel}>Expiry</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="MM/YY"
                  value={cardForm.expiry}
                  onChangeText={(t) => setCardForm({ ...cardForm, expiry: formatExpiry(t) })}
                  keyboardType="numeric"
                  maxLength={5}
                />
              </View>
              <View style={[styles.formGroup, { flex: 1, marginLeft: 8 }]}>
                <Text style={styles.formLabel}>CVV</Text>
                <TextInput
                  style={styles.formInput}
                  placeholder="123"
                  value={cardForm.cvv}
                  onChangeText={(t) => setCardForm({ ...cardForm, cvv: t.replace(/\D/g, '').substring(0, 4) })}
                  keyboardType="numeric"
                  maxLength={4}
                  secureTextEntry
                />
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Cardholder Name</Text>
              <TextInput
                style={styles.formInput}
                placeholder="John Doe"
                value={cardForm.name}
                onChangeText={(t) => setCardForm({ ...cardForm, name: t })}
                autoCapitalize="words"
              />
            </View>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setCardForm({ ...cardForm, isDefault: !cardForm.isDefault })}
            >
              <View style={[styles.checkbox, cardForm.isDefault && styles.checkboxChecked]}>
                {cardForm.isDefault && <Ionicons name="checkmark" size={16} color="#fff" />}
              </View>
              <Text style={styles.checkboxLabel}>Set as default payment method</Text>
            </TouchableOpacity>
          </ScrollView>
          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={[styles.modalButton, loading && styles.modalButtonDisabled]}
              onPress={handleAddPaymentMethod}
              disabled={loading}
            >
              {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalButtonText}>Add Card</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Cancel Modal
  const renderCancelModal = () => (
    <Modal
      visible={showCancelModal}
      transparent={false}
      animationType="slide"
      onRequestClose={() => setShowCancelModal(false)}
    >
      <SafeAreaView style={styles.cancelModalContainer}>
        <CancelSubscriptionScreen
          subscription={subscription}
          onCancel={() => setShowCancelModal(false)}
          onConfirm={confirmCancelSubscription}
        />
      </SafeAreaView>
    </Modal>
  );

  // Loading state
  if (loading && !subscription && !refreshing) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor={primary} />
        {renderHeader()}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={primary} />
          <Text style={styles.loadingText}>Loading payment information...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={primary} />
      {renderHeader()}
      {renderTabs()}
      <Animated.View style={[styles.content, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
        {activeTab === 'subscription' && renderSubscriptionTab()}
        {activeTab === 'methods' && renderPaymentMethodsTab()}
        {activeTab === 'history' && renderBillingHistoryTab()}
        {activeTab === 'features' && renderPremiumFeaturesTab()}
      </Animated.View>
      {renderUpgradeModal()}
      {renderAddCardModal()}
      {renderCancelModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: gray[50],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: primary,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
  },
  headerRight: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: gray[600],
  },
  content: {
    flex: 1,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: gray[200],
    position: 'relative',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
  },
  tabLabel: {
    fontSize: 13,
    fontWeight: '500',
    marginLeft: 6,
  },
  tabIndicator: {
    position: 'absolute',
    bottom: 0,
    height: 3,
    backgroundColor: primary,
    borderRadius: 3,
    alignSelf: 'center',
    left: 20,
  },
  tabContent: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  // Subscription tab
  currentSubscriptionCard: {
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  subscriptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
  },
  currentPlanLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 4,
  },
  currentPlanName: {
    fontSize: 24,
    fontWeight: '700',
    color: '#fff',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  billingInfo: {
    marginBottom: 20,
  },
  billingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  billingLabel: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    marginLeft: 6,
    marginRight: 6,
  },
  billingValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  subscriptionActions: {
    marginTop: 10,
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#fff',
  },
  upgradeButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  changeButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderWidth: 1,
    borderColor: '#fff',
  },
  changeButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  cancelButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: danger,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  usageCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  usageTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: gray[800],
    marginBottom: 15,
  },
  usageStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  usageStat: {
    alignItems: 'center',
  },
  usageNumber: {
    fontSize: 24,
    fontWeight: '700',
    color: primary,
    marginBottom: 4,
  },
  usageLabel: {
    fontSize: 12,
    color: gray[600],
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: gray[800],
    marginBottom: 16,
  },
  planCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    position: 'relative',
  },
  popularBadge: {
    position: 'absolute',
    top: -8,
    left: 20,
    backgroundColor: success,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  popularBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  planName: {
    fontSize: 20,
    fontWeight: '700',
    color: gray[800],
    marginBottom: 4,
  },
  planPriceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  planPrice: {
    fontSize: 24,
    fontWeight: '700',
  },
  planPeriod: {
    fontSize: 14,
    color: gray[500],
    marginLeft: 4,
  },
  currentPlanChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  currentPlanChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  selectPlanButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 25,
  },
  selectPlanButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  featureList: {
    marginTop: 8,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: gray[700],
    marginLeft: 8,
    flex: 1,
  },
  moreFeatures: {
    fontSize: 12,
    color: gray[500],
    marginTop: 4,
    fontStyle: 'italic',
  },
  // Payment Methods tab
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: primary,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: primary,
    marginLeft: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: gray[500],
    marginTop: 12,
  },
  paymentMethodCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  cardInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: gray[100],
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  cardDetails: {
    flex: 1,
  },
  cardNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: gray[800],
    marginBottom: 2,
  },
  cardExpiry: {
    fontSize: 14,
    color: gray[600],
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  defaultBadge: {
    backgroundColor: success,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  defaultBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
  },
  securityNote: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
    padding: 16,
    backgroundColor: gray[100],
    borderRadius: 12,
  },
  securityNoteText: {
    fontSize: 14,
    color: gray[700],
    marginLeft: 8,
    flex: 1,
  },
  // Billing History tab
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: primary,
  },
  downloadButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: primary,
    marginLeft: 4,
  },
  invoiceCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  invoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  invoiceDate: {
    fontSize: 16,
    fontWeight: '600',
    color: gray[800],
    marginBottom: 2,
  },
  invoiceDescription: {
    fontSize: 14,
    color: gray[600],
  },
  invoiceRight: {
    alignItems: 'flex-end',
  },
  invoiceAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: gray[800],
    marginBottom: 4,
  },
  invoiceStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  invoiceStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
    textTransform: 'capitalize',
  },
  invoiceNumber: {
    fontSize: 12,
    color: gray[500],
  },
  // Premium Features tab
  featureCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  featureLocked: {
    backgroundColor: gray[100],
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  featureInfo: {
    flex: 1,
    marginRight: 8,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: gray[800],
    marginBottom: 2,
  },
  featureTitleLocked: {
    color: gray[500],
  },
  featureDescription: {
    fontSize: 14,
    color: gray[600],
  },
  featureDescriptionLocked: {
    color: gray[400],
  },
  // Modals
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: gray[800],
  },
  modalPlanCard: {
    borderWidth: 1,
    borderColor: gray[200],
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  modalPlanSelected: {
    borderColor: primary,
    backgroundColor: gray[50],
  },
  modalPlanHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  modalPlanName: {
    fontSize: 18,
    fontWeight: '600',
    color: gray[800],
    marginBottom: 2,
  },
  modalPlanPrice: {
    fontSize: 20,
    fontWeight: '700',
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: gray[400],
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: primary,
  },
  radioInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: primary,
  },
  modalFeatureList: {
    paddingLeft: 8,
  },
  modalFeatureItem: {
    fontSize: 14,
    color: gray[600],
    marginBottom: 4,
  },
  modalFeatureMore: {
    fontSize: 12,
    color: gray[500],
    marginTop: 4,
  },
  modalFooter: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: gray[200],
  },
  modalButton: {
    backgroundColor: primary,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  modalButtonDisabled: {
    backgroundColor: gray[400],
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  // Form elements
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: gray[700],
    marginBottom: 6,
  },
  formInput: {
    borderWidth: 1,
    borderColor: gray[300],
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: gray[800],
    backgroundColor: '#fff',
  },
  formRow: {
    flexDirection: 'row',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 16,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: gray[400],
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  checkboxChecked: {
    backgroundColor: primary,
    borderColor: primary,
  },
  checkboxLabel: {
    fontSize: 14,
    color: gray[700],
  },
  cancelModalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
});